import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Paper,
  Slide,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ClassIcon from "@material-ui/icons/Class";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/styles";
import managerClassApi from "api/managerClass";
import { useAppDispatch } from "app/hooks";
import InputField from "components/FormFields/InputField";
import { ResponseStatus } from "models/common";
import { CreateClass } from "models/managerClass";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as yup from "yup";
import { fetchClassList, selectFilterClassList } from "../../managerClassSlice";

export default function AddClass() {
  const useStyles = makeStyles(() => ({
    btnTrigger: {
      background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
      color: "#FFFFFF",
      borderRadius: 12,
      height: 40,
      padding: "0 20px",
      fontWeight: 700,
      fontSize: "0.875rem",
      textTransform: "none",
      boxShadow: "0 4px 14px rgba(37, 99, 235, 0.35)",
      transition: "all 0.2s ease-in-out",
      whiteSpace: "nowrap",
      zIndex: 2,
      "&:hover": {
        background: "linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)",
        boxShadow: "0 6px 20px rgba(37, 99, 235, 0.45)",
        transform: "translateY(-1px)",
      },
    },
    modalContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(15, 23, 42, 0.5)",
    },
    modalCard: {
      width: 540,
      maxWidth: "92vw",
      borderRadius: 24,
      backgroundColor: "#FFFFFF",
      boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
      border: "1px solid #E2E8F0",
      outline: "none",
      overflow: "hidden",
    },
    modalHeader: {
      background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)",
      color: "#FFFFFF",
      padding: "18px 24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    modalBody: {
      padding: "24px 28px",
    },
    actionGroup: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 12,
      marginTop: 24,
    },
    btnCancel: {
      borderRadius: 10,
      padding: "9px 24px",
      fontSize: "0.875rem",
      fontWeight: 700,
      textTransform: "none",
      color: "#475569",
      borderColor: "#CBD5E1",
      "&:hover": {
        backgroundColor: "#F8FAFC",
        borderColor: "#94A3B8",
      },
    },
    btnSubmit: {
      background: "linear-gradient(135deg, #0F172A 0%, #312E81 100%)",
      borderRadius: 10,
      padding: "9px 28px",
      fontSize: "0.875rem",
      fontWeight: 700,
      textTransform: "none",
      color: "#FFFFFF !important",
      boxShadow: "0 4px 14px rgba(15, 23, 42, 0.2)",
      transition: "all 0.2s ease",
      "&:hover": {
        background: "linear-gradient(135deg, #1E1B4B 0%, #4338CA 100%)",
        boxShadow: "0 6px 20px rgba(15, 23, 42, 0.3)",
      },
    },
  }));

  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const filterClassList = useSelector(selectFilterClassList);
  const [open, setOpen] = useState(false);

  const schema = yup.object().shape({
    name: yup.string().required("*Tên lớp học không được để trống"),
  });

  const initialValues: CreateClass = {
    name: "",
    description: "",
  };

  const { handleSubmit, control, reset } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(schema),
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    reset(initialValues);
  };

  const handleSubmitForm = async (value: CreateClass) => {
    try {
      const res: ResponseStatus = await managerClassApi.createClass(value);
      if (res.responseCode === "00") {
        toast.success(`${t("managerClass.themclassthanhcong")}`);
        dispatch(fetchClassList(filterClassList));
        handleClose();
      } else {
        if (res.responseCode === "30") {
          toast.error(`${t("managerClass.classdatontai")}`);
          throw new Error("Create class is failed");
        }
      }
    } catch (error) { }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddIcon style={{ fontSize: 20 }} />}
        className={classes.btnTrigger}
        onClick={handleOpen}
      >
        {t("button.addnew")}
      </Button>

      <Modal
        open={open}
        onClose={handleClose}
        className={classes.modalContainer}
        closeAfterTransition
      >
        <Slide direction="down" in={open} mountOnEnter unmountOnExit>
          <Paper className={classes.modalCard} elevation={0}>
            <Box className={classes.modalHeader}>
              <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <ClassIcon style={{ color: "#818CF8", fontSize: 24 }} />
                <Typography variant="h6" style={{ fontWeight: 800, color: "#FFFFFF", fontSize: "1.125rem" }}>
                  {t("managerClass.themclass")}
                </Typography>
              </Box>
              <IconButton onClick={handleClose} style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <form onSubmit={handleSubmit(handleSubmitForm)}>
              <Box className={classes.modalBody}>
                <InputField
                  control={control}
                  label={t("managerClass.tenclass") + " *"}
                  name="name"
                  size="small"
                  borderRadius="12px"
                  required={true}
                />

                <Box mt={2}>
                  <InputField
                    control={control}
                    label="Thông tin / Mô tả lớp học"
                    name="description"
                    size="small"
                    borderRadius="12px"
                    multiline
                    rows={3}
                  />
                </Box>

                <Box className={classes.actionGroup}>
                  <Button
                    variant="outlined"
                    className={classes.btnCancel}
                    onClick={handleClose}
                  >
                    {t("button.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    className={classes.btnSubmit}
                    startIcon={<AddIcon style={{ fontSize: 18 }} />}
                  >
                    {t("button.addnew")}
                  </Button>
                </Box>
              </Box>
            </form>
          </Paper>
        </Slide>
      </Modal>
    </>
  );
}
